// In requiredAuth.tsx - Clean production version with session timeout fix

import {useDispatch,useSelector} from "react-redux";
import {Navigate, Outlet, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import React, {useEffect, useRef} from "react";
import moment from 'moment';
import {SESSION_TIME} from "../utils/config";
import authService from "../services/authService";
import {RootState} from "../store"
import { setOTPVerified } from "../slices/authReducer";

export const RequireAuth = () => {

    const { isLoggedIn, sessionTime, data} = useSelector((state:RootState) => state.authUser);
    let location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const [hasCheckedAuth, setHasCheckedAuth] = React.useState(false);
    
    const authCheckInProgress = useRef(false);
    const hasPerformedInitialCheck = useRef(false);

    // Helper function to get valid tokens from AUTH_LOGIN and PERSIST_OBJECT
    const getAuthStatus = () => {
      const authLogin = localStorage.getItem("AUTH_LOGIN");
      const persistObject = localStorage.getItem("PERSIST_OBJECT");
      const useRefreshFlag = localStorage.getItem("useRefresh");
      
      let hasValidToken = false;
      let tokenSource = "";
      let ssoToken = null;
      
      // Check AUTH_LOGIN for ssoToken
      if (authLogin) {
        try {
          const parsed = JSON.parse(authLogin);
          if (parsed.ssoToken) {
            hasValidToken = true;
            tokenSource = "AUTH_LOGIN.ssoToken";
            ssoToken = parsed.ssoToken;
          }
        } catch (e) {
          console.log("Error parsing AUTH_LOGIN:", e);
        }
      }
      
      // Check if we have refresh capability
      const hasRefreshCapability = !!(persistObject && useRefreshFlag);
      
      return {
        hasValidToken,
        hasRefreshCapability,
        tokenSource,
        ssoToken,
        hasAnyValidAuth: hasValidToken || hasRefreshCapability
      };
    };

    function checkSession() {
        if (authCheckInProgress.current) {
            return;
        }
        
        authCheckInProgress.current = true;
        
        const authStatus = getAuthStatus();
        const mpinAuthSession = sessionStorage.getItem("MPIN_AUTH_SESSION");
        const mpinAuthenticated = sessionStorage.getItem("MPIN_AUTHENTICATED");
        const comingFromMpin = sessionStorage.getItem("COMING_FROM_MPIN");
        const mpinNavigationSuccess = sessionStorage.getItem("MPIN_NAVIGATION_SUCCESS");
        const currentPath = location.pathname;
        
        try {
            if (!hasPerformedInitialCheck.current) {
                setHasCheckedAuth(true);
                hasPerformedInitialCheck.current = true;
            }
            
            if (currentPath === "/apps") {
                // Validate authentication tokens
                if (!authStatus.hasAnyValidAuth) {
                    authCheckInProgress.current = false;
                    handleCompleteLogout();
                    return;
                }
                
                // Allow direct access for QR authentication
                if (data && data.mfaType === 'qr') {
                    authCheckInProgress.current = false;
                    return;
                }
                
                // Check for recent successful MPIN navigation
                if (mpinNavigationSuccess) {
                    setTimeout(() => {
                        sessionStorage.removeItem("MPIN_NAVIGATION_SUCCESS");
                    }, 10000);
                    
                    authCheckInProgress.current = false;
                    return;
                }
                
                // Redirect to MPIN if no fresh authentication
                if (!comingFromMpin) {
                    sessionStorage.removeItem("MPIN_AUTH_SESSION");
                    sessionStorage.removeItem("MPIN_AUTHENTICATED");
                    sessionStorage.removeItem("MPIN_NAVIGATION_SUCCESS");

                    authCheckInProgress.current = false;
                    navigate('/MPin', {state: {returnTo: currentPath}, replace: true});
                    return;
                }
                
                // CRITICAL FIX: Skip session time check if user just came from MPIN
                if (comingFromMpin) {
                    setTimeout(() => {
                        if (window.location.pathname === "/apps") {
                            const stillComingFromMpin = sessionStorage.getItem("COMING_FROM_MPIN");
                            if (stillComingFromMpin) {
                                sessionStorage.removeItem("COMING_FROM_MPIN");
                            }
                        }
                    }, 15000);
                    
                    // Skip session time check for fresh MPIN authentication
                    authCheckInProgress.current = false;
                    return;
                }
            }

            // Only check session validity if NOT coming from MPIN
            if (sessionTime && !comingFromMpin) {
                var now = moment();
                var momentInTime = moment(sessionTime);
                var diff = now.diff(momentInTime, 'minutes');
                
                const SESSION_TIMEOUT = 480; // 8 hours for general session
                
                if (diff > SESSION_TIMEOUT) {
                    authCheckInProgress.current = false;
                    
                    if (data != undefined && data.mfaType == 'qr') {
                        handleQRLogout();
                        return;
                    } else {
                        handleCompleteLogout();
                        return;
                    }
                }
            }
            
        } finally {
            authCheckInProgress.current = false;
        }
    }

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (!authCheckInProgress.current) {
                checkSession();
            }
        }, 100);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [location.pathname]);

    const handleCompleteLogout = () => {
        // Clear all sessions
        sessionStorage.removeItem("MPIN_AUTH_SESSION");
        sessionStorage.removeItem("MPIN_AUTHENTICATED");
        sessionStorage.removeItem("COMING_FROM_MPIN");
        sessionStorage.removeItem("MPIN_NAVIGATION_SUCCESS");
        
        // Clear authentication storage
        localStorage.removeItem("PERSIST_OBJECT");
        localStorage.removeItem("AUTH_LOGIN");
        localStorage.removeItem("useRefresh");
        
        dispatch(authService.logout());
        navigate("/", { replace: true });
    }

    const handleQRLogout = () => {
        sessionStorage.removeItem("MPIN_AUTH_SESSION");
        sessionStorage.removeItem("MPIN_AUTHENTICATED");
        sessionStorage.removeItem("COMING_FROM_MPIN");
        sessionStorage.removeItem("MPIN_NAVIGATION_SUCCESS");
        
        dispatch(authService.logout());
        navigate("/qrLogin", { replace: true });
    }

    // Redirect to appropriate page based on authentication status
    if (!isLoggedIn) {
        const authStatus = getAuthStatus();
        
        // If user has valid tokens, redirect to MPIN instead of login
        if (authStatus.hasAnyValidAuth) {
            return <Navigate to="/MPin" state={{ returnTo: location.pathname }} replace />;
        }
        
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return <Outlet />;
};